import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { BreadcrumbHeading, Spinner, Typography } from '@weareopenr/component-library';
import { Routes } from 'src/routes';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { usePublishHistory } from 'src/hooks/usePublishHistory';
import { ScheduleCard } from 'src/components/ScheduleCard';
import CollapsibleSection from 'src/components/CollapsibleSection';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { groupPublishingByWeek } from 'src/utils/schedules/groupPublishingByWeek';
import { getPublishCardDetails } from 'src/utils/schedules/getPublishCardDetails';
import styles from './PublishHistory.module.scss';

export const PublishHistory = () => {
  const { trackEvent } = useUserTracking();
  const navigate = useNavigate();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = usePublishHistory();

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage) fetchNextPage();
    },
  });

  useEffect(() => {
    document.title = 'Publish History';
    trackEvent('page_viewed', { page: 'publish_history' });
  }, [trackEvent]);

  const allItems = data?.pages.flatMap((page) => page.data) ?? [];
  const weeks = groupPublishingByWeek(allItems, 'desc');

  return (
    <div className={styles['page-container']}>
      <div className={styles['page-header']}>
        <BreadcrumbHeading breadcrumbs={[{ label: 'Publish History', path: Routes.PUBLISH_HISTORY }]} />
        <div className={styles['filters']}>{/* filters */}</div>
      </div>
      <div className={styles['history']}>
        {isLoading && <SkeletonContainer hiddenTitle />}
        {weeks.map(({ key, title, items }) => (
          <CollapsibleSection
            key={key}
            title={title}
            size="medium"
            defaultExpanded
            bordered
            arrowPosition="left"
            arrowColour="liquorice"
            scrollable={false}
          >
            <div className={styles['publish-group']}>
              {items.map((item) => (
                <div key={item.event_id}>
                  <ScheduleCard
                    type={item.event_type}
                    name={item.name}
                    scheduledTime={item.created_at}
                    status={item.status}
                    details={getPublishCardDetails(item, item.name)}
                    isHistoric
                    actions={{
                      onViewStatus: () =>
                        navigate(`${Routes.PUBLISH_HISTORY}/status/${item.event_id}`, {
                          state: { entityName: item.entity_name },
                        }),
                    }}
                  />
                </div>
              ))}
            </div>
          </CollapsibleSection>
        ))}
        {!isLoading && weeks.length === 0 && (
          <div className={styles['no-history']}>
            <Typography>No publish history yet.</Typography>
          </div>
        )}
        {isFetchingNextPage && (
          <div className={styles['spinner-container']}>
            <Spinner size="medium" colour="rainbow" />
          </div>
        )}
        {hasNextPage && !isFetchingNextPage && <div ref={ref} />}
      </div>
    </div>
  );
};
